<template>
  <section class="layers-panel">
    <header class="rail-head">
      <h4 class="gm-label">Layers</h4>
      <span class="layers-count">{{ activeCount }}/{{ layers.length }}</span>
    </header>

    <p class="layers-hint">Badges run along the top edge of a room in this order.</p>

    <ul class="layers-list">
      <li v-for="layer in layers" :key="layer.id">
        <button
            :class="['layer-row', { 'is-off': !isActive(layer.id) }]"
            :aria-pressed="isActive(layer.id)"
            :title="isActive(layer.id) ? `Hide ${layer.name} on the map` : `Show ${layer.name} on the map`"
            @click="$emit('layer-toggled', layer.id)"
        >
          <span class="layer-swatch" :style="{ background: layer.color, borderRadius: layer.shape === 'round' ? '50%' : '3px' }"></span>
          <span class="layer-name">{{ layer.name }}</span>
          <span class="layer-state">{{ isActive(layer.id) ? 'On' : 'Off' }}</span>
        </button>
      </li>
    </ul>
  </section>
</template>

<script>
import {WorldEditorConfig, WorldEditorLayer} from '@/models/WorldEditorModels.ts';

export default {
  name: 'WorldEditorLayersPanel',
  props: {
    activeLayers: {
      type: Set,
      default: () => new Set()
    }
  },
  emits: ['layer-toggled'],
  computed: {
    // Same order the room badges are drawn in, so the list doubles as the legend.
    layers() {
      const colors = WorldEditorConfig.layerColors;
      return [
        {id: WorldEditorLayer.PLAYERS, name: 'Players', shape: 'round', color: colors[WorldEditorLayer.PLAYERS]},
        {id: WorldEditorLayer.ANOMALIES, name: 'Anomalies', shape: 'round', color: colors[WorldEditorLayer.ANOMALIES]},
        {id: WorldEditorLayer.NPCS, name: 'NPCs', shape: 'square', color: colors[WorldEditorLayer.NPCS]},
        {id: WorldEditorLayer.OBJECTS, name: 'Items', shape: 'square', color: colors[WorldEditorLayer.OBJECTS]},
        {id: WorldEditorLayer.SPAWNERS, name: 'Spawners', shape: 'round', color: colors[WorldEditorLayer.SPAWNERS]}
      ];
    },
    activeCount() {
      return this.layers.filter(layer => this.isActive(layer.id)).length;
    }
  },
  methods: {
    isActive(layer) {
      return this.activeLayers.has(layer);
    }
  }
};
</script>

<style scoped>
.layers-panel {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

.rail-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--gm-gap-sm);
}

.rail-head .gm-label {
  margin: 0;
}

.layers-count {
  font-family: var(--gm-font-mono);
  font-size: var(--gm-text-micro);
  color: var(--gm-text-dim);
  font-variant-numeric: tabular-nums;
}

.layers-hint {
  margin: 0;
  font-size: var(--gm-text-micro);
  line-height: var(--gm-leading-tight);
  color: var(--gm-text-dim);
}

.layers-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.layer-row {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  width: 100%;
  padding: 0.3rem 0.4rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--gm-radius-sm);
  color: var(--gm-text);
  cursor: pointer;
  text-align: left;
  transition: background var(--gm-duration) var(--gm-ease), color var(--gm-duration) var(--gm-ease);
}

.layer-row:hover {
  background: var(--gm-accent-softer);
}

.layer-row:focus-visible {
  outline: none;
  border-color: var(--gm-border-hi);
}

.layer-swatch {
  width: 11px;
  height: 11px;
  flex: none;
  border: 1px solid rgba(0, 0, 0, 0.6);
}

.layer-name {
  flex: 1;
  font-size: var(--gm-text-xs);
}

.layer-state {
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-micro);
  font-weight: 600;
  letter-spacing: var(--gm-track-label);
  text-transform: uppercase;
  color: var(--gm-accent);
}

.layer-row.is-off {
  color: var(--gm-text-dim);
}

.layer-row.is-off .layer-swatch {
  background: var(--gm-surface-2) !important;
  border-color: var(--gm-border-dim);
}

.layer-row.is-off .layer-state {
  color: var(--gm-text-dim);
}
</style>
